import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SwipeableDrawer,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createIncident } from 'api/canvms/incidents';
import type {
  CreateIncidentInput,
  Incident,
  IncidentSeverity,
  VMSCamera,
} from '../../types/canvision.types';
import { useSites } from '../../hooks/useSites';
import { useCameras } from '../../hooks/useCameras';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DRAWER_WIDTH = 450;

const INCIDENT_TYPES = [
  { value: 'person', label: 'Person Detected' },
  { value: 'vehicle', label: 'Vehicle Detected' },
  { value: 'motion', label: 'Motion Detected' },
  { value: 'face', label: 'Face Detected' },
  { value: 'animal', label: 'Animal Detected' },
  { value: 'intrusion', label: 'Intrusion' },
  { value: 'loitering', label: 'Loitering' },
];

const SEVERITY_OPTIONS: { value: IncidentSeverity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CreateIncidentDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated: (incident: Incident) => void;
  siteIds: number[];
  cameras: VMSCamera[];
}

// ---------------------------------------------------------------------------
// Form content (shared between drawer and dialog)
// ---------------------------------------------------------------------------

interface FormContentProps {
  /** Form state & handlers — passed through from the parent component */
  selectedSiteId: number | '';
  onSiteChange: (siteId: number | '') => void;
  selectedCameraId: number | '';
  onCameraChange: (cameraId: number | '') => void;
  incidentType: string;
  onIncidentTypeChange: (value: string) => void;
  severity: IncidentSeverity;
  onSeverityChange: (value: IncidentSeverity) => void;
  title: string;
  onTitleChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  clipStartTime: string;
  onClipStartTimeChange: (value: string) => void;
  clipEndTime: string;
  onClipEndTimeChange: (value: string) => void;
  errors: Record<string, string | null>;
  onClearError: (field: string) => void;
  submitError: string | null;
  onClearSubmitError: () => void;
  sites: { id: number; name: string; display_name: string | null }[];
  sitesLoading: boolean;
  siteCameras: VMSCamera[];
  camerasLoading: boolean;
}

function FormContent({
  selectedSiteId,
  onSiteChange,
  selectedCameraId,
  onCameraChange,
  incidentType,
  onIncidentTypeChange,
  severity,
  onSeverityChange,
  title,
  onTitleChange,
  description,
  onDescriptionChange,
  clipStartTime,
  onClipStartTimeChange,
  clipEndTime,
  onClipEndTimeChange,
  errors,
  onClearError,
  submitError,
  onClearSubmitError,
  sites,
  sitesLoading,
  siteCameras,
  camerasLoading,
}: FormContentProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {submitError && (
        <Alert severity="error" onClose={onClearSubmitError}>
          {submitError}
        </Alert>
      )}

      {/* Site */}
      <FormControl fullWidth required error={!!errors.siteId}>
        <InputLabel>Site</InputLabel>
        <Select
          value={selectedSiteId}
          label="Site"
          onChange={(e) => onSiteChange(e.target.value as number | '')}
          disabled={sitesLoading}
        >
          {sites.map((site) => (
            <MenuItem key={site.id} value={site.id}>
              {site.display_name || site.name}
            </MenuItem>
          ))}
        </Select>
        {errors.siteId && <FormHelperText>{errors.siteId}</FormHelperText>}
      </FormControl>

      {/* Camera */}
      <FormControl
        fullWidth
        required
        error={!!errors.cameraId}
        disabled={!selectedSiteId || camerasLoading}
      >
        <InputLabel>Camera</InputLabel>
        <Select
          value={selectedCameraId}
          label="Camera"
          onChange={(e) => {
            onCameraChange(e.target.value as number | '');
            if (errors.cameraId) onClearError('cameraId');
          }}
        >
          {siteCameras.map((cam) => (
            <MenuItem key={cam.id} value={cam.id}>
              {cam.display_name || cam.name || `Camera ${cam.id}`}
            </MenuItem>
          ))}
        </Select>
        {errors.cameraId && (
          <FormHelperText>{errors.cameraId}</FormHelperText>
        )}
      </FormControl>

      {/* Incident Type */}
      <FormControl fullWidth required error={!!errors.incidentType}>
        <InputLabel>Incident Type</InputLabel>
        <Select
          value={incidentType}
          label="Incident Type"
          onChange={(e) => {
            onIncidentTypeChange(e.target.value);
            if (errors.incidentType) onClearError('incidentType');
          }}
        >
          {INCIDENT_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
        {errors.incidentType && (
          <FormHelperText>{errors.incidentType}</FormHelperText>
        )}
      </FormControl>

      {/* Severity */}
      <FormControl fullWidth>
        <InputLabel>Severity</InputLabel>
        <Select
          value={severity}
          label="Severity"
          onChange={(e) =>
            onSeverityChange(e.target.value as IncidentSeverity)
          }
        >
          {SEVERITY_OPTIONS.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Title */}
      <TextField
        label="Title"
        required
        fullWidth
        value={title}
        onChange={(e) => {
          onTitleChange(e.target.value);
          if (errors.title) onClearError('title');
        }}
        error={!!errors.title}
        helperText={errors.title}
        inputProps={{ maxLength: 200 }}
      />

      {/* Description */}
      <TextField
        label="Description"
        fullWidth
        multiline
        rows={3}
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
      />

      {/* Clip times */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          type="datetime-local"
          label="Clip Start"
          fullWidth
          value={clipStartTime}
          onChange={(e) => onClipStartTimeChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="datetime-local"
          label="Clip End"
          fullWidth
          value={clipEndTime}
          onChange={(e) => {
            onClipEndTimeChange(e.target.value);
            if (errors.clipEndTime) onClearError('clipEndTime');
          }}
          error={!!errors.clipEndTime}
          helperText={errors.clipEndTime}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function CreateIncidentDrawer({
  open,
  onClose,
  onCreated,
  siteIds,
}: CreateIncidentDrawerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // -------------------------------------------------------------------------
  // Form state
  // -------------------------------------------------------------------------

  const [selectedSiteId, setSelectedSiteId] = useState<number | ''>('');
  const [selectedCameraId, setSelectedCameraId] = useState<number | ''>('');
  const [incidentType, setIncidentType] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [clipStartTime, setClipStartTime] = useState('');
  const [clipEndTime, setClipEndTime] = useState('');

  // Validation state
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Data hooks
  // -------------------------------------------------------------------------

  const { sites, loading: sitesLoading } = useSites();
  const { cameras: siteCameras, loading: camerasLoading } = useCameras(
    selectedSiteId ? Number(selectedSiteId) : null
  );

  // -------------------------------------------------------------------------
  // Reset form when opened
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (open) {
      setIncidentType('');
      setSeverity('medium');
      setTitle('');
      setDescription('');
      setClipStartTime('');
      setClipEndTime('');
      setErrors({});
      setSubmitError(null);
      setSelectedCameraId('');

      // Pre-select site if only one in context
      if (siteIds.length === 1) {
        setSelectedSiteId(siteIds[0]);
      } else {
        setSelectedSiteId('');
      }
    }
  }, [open, siteIds]);

  // Auto-select camera when site cameras load and there's only one
  useEffect(() => {
    if (siteCameras.length === 1) {
      setSelectedCameraId(siteCameras[0].id);
    }
  }, [siteCameras]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleSiteChange = (siteId: number | '') => {
    setSelectedSiteId(siteId);
    setSelectedCameraId('');
    if (errors.siteId) setErrors((prev) => ({ ...prev, siteId: null }));
  };

  const handleClearError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string | null> = {};

    if (!selectedSiteId) {
      newErrors.siteId = 'Site is required';
    }
    if (!selectedCameraId) {
      newErrors.cameraId = 'Camera is required';
    }
    if (!incidentType) {
      newErrors.incidentType = 'Incident type is required';
    }
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.trim().length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }
    if (clipStartTime && clipEndTime) {
      if (new Date(clipEndTime) <= new Date(clipStartTime)) {
        newErrors.clipEndTime = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((v) => v === null || v === undefined);
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);
      setSubmitError(null);

      const input: CreateIncidentInput = {
        cameraId: Number(selectedCameraId),
        siteId: Number(selectedSiteId),
        incidentType,
        severity,
        title: title.trim(),
        description: description.trim() || undefined,
        clipStartTime: clipStartTime || undefined,
        clipEndTime: clipEndTime || undefined,
      };

      const response = await createIncident(input);
      onCreated(response.data);
      onClose();
    } catch (err: any) {
      console.error('[CANVMS] Error creating incident:', err);
      setSubmitError(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          err.message ||
          'Failed to create incident'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  // no-op for SwipeableDrawer onOpen (required prop)
  const handleOpen = () => {};

  // -------------------------------------------------------------------------
  // Shared form content props
  // -------------------------------------------------------------------------

  const formContentProps: FormContentProps = {
    selectedSiteId,
    onSiteChange: handleSiteChange,
    selectedCameraId,
    onCameraChange: setSelectedCameraId,
    incidentType,
    onIncidentTypeChange: setIncidentType,
    severity,
    onSeverityChange: setSeverity,
    title,
    onTitleChange: setTitle,
    description,
    onDescriptionChange: setDescription,
    clipStartTime,
    onClipStartTimeChange: setClipStartTime,
    clipEndTime,
    onClipEndTimeChange: setClipEndTime,
    errors,
    onClearError: handleClearError,
    submitError,
    onClearSubmitError: () => setSubmitError(null),
    sites,
    sitesLoading,
    siteCameras,
    camerasLoading,
  };

  // -------------------------------------------------------------------------
  // Action buttons (shared between drawer and dialog)
  // -------------------------------------------------------------------------

  const actionButtons = (
    <>
      <Button onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={16} /> : undefined}
      >
        {submitting ? 'Creating...' : 'Create Incident'}
      </Button>
    </>
  );

  // -------------------------------------------------------------------------
  // Mobile: Dialog (preserve current behavior)
  // -------------------------------------------------------------------------

  if (isMobile) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Manual Incident</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormContent {...formContentProps} />
          </Box>
        </DialogContent>
        <DialogActions>{actionButtons}</DialogActions>
      </Dialog>
    );
  }

  // -------------------------------------------------------------------------
  // Desktop: SwipeableDrawer anchor="right"
  // -------------------------------------------------------------------------

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      onOpen={handleOpen}
      disableSwipeToOpen
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="h6">Create Manual Incident</Typography>
        <IconButton
          onClick={handleClose}
          disabled={submitting}
          size="small"
          aria-label="Close drawer"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Scrollable form body */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
        }}
      >
        <FormContent {...formContentProps} />
      </Box>

      <Divider />

      {/* Footer actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          px: 2,
          py: 1.5,
        }}
      >
        {actionButtons}
      </Box>
    </SwipeableDrawer>
  );
}

export default CreateIncidentDrawer;
